import React, { useEffect, useRef, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MessagesSquare, Send, Loader2 } from "lucide-react";

const fmt = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; } };
const SOURCE_LABEL = { chat: "Website chat", meeting: "Meeting chat", portal: "Message" };

export default function ClientMessages() {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get("/portal/client/messages").then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items]);

  const send = async () => {
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try { const { data } = await api.post("/portal/client/messages", { body }); setItems((p) => [...(p || []), data.message]); setText(""); toast.success("Sent to your guide."); }
    catch (e) { toast.error(errMsg(e, "Could not send your message.")); }
    finally { setSending(false); }
  };

  return (
    <div data-testid="client-messages-page" className="flex flex-col h-[calc(100vh-160px)]">
      <div className="mb-3">
        <h2 className="font-display text-2xl font-bold text-[#252221] flex items-center gap-2"><MessagesSquare size={22} /> Messages</h2>
        <p className="text-[15px] text-muted-foreground">Talk to your MovingMyMama guide. We're here whenever you need us.</p>
      </div>

      <Card className="flex-1 overflow-y-auto p-4 border-[#E6EDF2] space-y-3" data-testid="client-messages-thread">
        {items === null ? (
          <><Skeleton className="h-12 w-2/3" /><Skeleton className="h-12 w-1/2 ml-auto" /></>
        ) : items.length === 0 ? (
          <div className="text-center text-muted-foreground text-[15px] py-10">No messages yet. Say hello — your guide will get back to you.</div>
        ) : (
          items.map((m, i) => {
            const mine = m.from === "you" || m.from === "client";
            return (
              <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? "bg-[#1C4358] text-white" : "bg-[#F1F5F8] text-[#252221]"}`}>
                  {!mine && <div className="text-[11px] font-semibold text-[#1C4358]/70 mb-0.5">{m.from_name || "Your guide"} · {SOURCE_LABEL[m.source] || ""}</div>}
                  <div className="text-[15px] whitespace-pre-line leading-relaxed">{m.body}</div>
                  <div className={`text-[11px] mt-1 ${mine ? "text-white/60" : "text-[#9AA5B1]"}`}>{fmt(m.at)}</div>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </Card>

      <div className="mt-3 flex gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Write a message to your guide…" data-testid="client-message-input"
          className="flex-1 h-12 rounded-full border border-[#D6E0E7] px-4 text-[15px] outline-none focus:border-[#1C4358]" />
        <Button onClick={send} disabled={sending || !text.trim()} className="bg-[#9C1E22] hover:bg-[#861A1D] text-white rounded-full h-12 px-5 gap-1.5" data-testid="client-message-send-btn">
          {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send
        </Button>
      </div>
    </div>
  );
}
